import React, { createContext, useContext, useState, useCallback } from 'react';
import ModernNotification from "../utils/Notification";

// Contexte de notification
const NotificationContext = createContext({
    showNotification: () => {},
    showSuccess: () => {},
    showError: () => {},
    showWarning: () => {},
    showInfo: () => {},
    showLove: () => {},
    showPremium: () => {},
});

let nextId = 0;

// Provider de notification corrigé
export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);

    const showNotification = useCallback(({
                                              type = 'info',
                                              message,
                                              title,
                                              duration = 5000,
                                              autoClose = true,
                                              onClick,
                                              position = 'top-right',
                                              minimizeOnExpire = false // Nouveau paramètre
                                          }) => {
        const id = nextId++;


        setNotifications([
            { id, type, message, title, duration, autoClose, onClick, position, minimizeOnExpire }
        ]);

        return id;
    }, []);

    const closeNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, []);

    const showSuccess = useCallback((message, options = {}) => {
        return showNotification({
            type: 'success',
            message,
            title: options.title || 'Succès',
            ...options
        });
    }, [showNotification]);

    const showError = useCallback((message, options = {}) => {
        return showNotification({
            type: 'error',
            message,
            title: options.title || 'Erreur',
            ...options
        });
    }, [showNotification]);

    const showWarning = useCallback((message, options = {}) => {
        return showNotification({
            type: 'warning',
            message,
            title: options.title || 'Attention',
            ...options
        });
    }, [showNotification]);

    const showInfo = useCallback((message, options = {}) => {
        return showNotification({
            type: 'info',
            message,
            title: options.title || 'Information',
            ...options
        });
    }, [showNotification]);

    const showLove = useCallback((message, options = {}) => {
        return showNotification({
            type: 'love',
            message,
            title: options.title || 'Message spécial',
            ...options
        });
    }, [showNotification]);

    const showPremium = useCallback((message, options = {}) => {
        return showNotification({
            type: 'premium',
            message,
            title: options.title || 'Premium',
            ...options
        });
    }, [showNotification]);

    const value = React.useMemo(() => ({
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        showLove,
        showPremium,
    }), [showNotification, showSuccess, showError, showWarning, showInfo, showLove, showPremium]);
Reflect.set(window, 'showNotificationGlobal', showNotification);

    return (
        <NotificationContext.Provider value={value}>
            {children}

            {/* Container des notifications TOP-RIGHT */}
            <div className="fixed top-4 right-4 z-[2000000] pointer-events-none flex flex-col items-end space-y-3">
                {notifications.filter(n => n.position === 'top-right').map((notification) => (
                    <div key={notification.id} className="pointer-events-auto">
                        <ModernNotification
                            id={notification.id}
                            onClose={closeNotification}
                            type={notification.type}
                            message={notification.message}
                            title={notification.title}
                            duration={notification.duration}
                            autoClose={notification.autoClose}
                            onClick={notification.onClick}
                            minimizeOnExpire={notification.minimizeOnExpire}
                        />
                    </div>
                ))}
            </div>

            {/* Container des notifications BOTTOM-RIGHT (pour le Chat) */}
            <div className="fixed bottom-4 right-4 z-[2000000] pointer-events-none flex flex-col items-end space-y-3">
                {notifications.filter(n => n.position === 'bottom-right').map((notification) => (
                    <div key={notification.id} className="pointer-events-auto">
                        <ModernNotification
                            id={notification.id}
                            onClose={closeNotification}
                            type={notification.type}
                            message={notification.message}
                            title={notification.title}
                            duration={notification.duration}
                            autoClose={notification.autoClose}
                            onClick={notification.onClick}
                            minimizeOnExpire={notification.minimizeOnExpire}
                        />
                    </div>
                ))}
            </div>
        </NotificationContext.Provider>
    );
};

// HOOK MANQUANT - Ajout du hook useNotification
export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};